import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { DeleteIcon, LocationTwoIcon, PenIcon, PlusIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { createOtoIndividualPlace } from '../../../api/journey-place/create-oto-individual-place'
import { deleteOtoIndividualPlace } from '../../../api/journey-place/delete-oto-individual-place'
import { getOtoIndividualPlaces } from '../../../api/journey-place/get-oto-individual-places'
import { updateOtoIndividualPlace } from '../../../api/journey-place/update-oto-individual-place'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { OtoIndividualPlace } from '../../../lib/types/models/journey-place/oto-individual-place'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import OtoIndividualPlaceForm from './oto-individual-place-form'

type Props = {
  journeyPlaceId: number
}

const JourneyPlaceOtoIndividualPlaces = ({ journeyPlaceId }: Props) => {
  const queryClient = useQueryClient()
  const queryKey = ['journey-place', journeyPlaceId, 'oto-individual-places']

  const { modalState, toggleModal, closeModel, selectedData } = useModalManager<
    'create' | 'update' | 'delete',
    OtoIndividualPlace
  >(['create', 'update', 'delete'])

  const { data: otoIndividualPlaces } = useQuery({
    queryKey,
    queryFn: () => getOtoIndividualPlaces(journeyPlaceId)
  })

  const { mutateAsync: createOtoIndividualPlaceMutation } = useMutation({
    mutationFn: createOtoIndividualPlace,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      toast.success("Lieu d'arrivée créé avec succès")
    }
  })

  const { mutateAsync: updateOtoIndividualPlaceMutation } = useMutation({
    mutationFn: updateOtoIndividualPlace,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      toast.success("Lieu d'arrivée modifié avec succès")
    }
  })

  const { isPending: isDeleting, mutate: deleteOtoIndividualPlaceMutation } = useMutation({
    mutationFn: deleteOtoIndividualPlace,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      toast.success("Lieu d'arrivée supprimé avec succès")
      closeModel('delete')
    }
  })

  const columns: Column<OtoIndividualPlace>[] = [
    {
      accessorKey: 'title',
      header: 'Intitulé',
      enableSorting: true,
      cell: (data) => data.row.original.title
    },
    {
      accessorKey: 'code',
      header: 'Code IATA',
      enableSorting: false,
      cell: (data) => data.row.original.code
    },
    {
      accessorKey: 'id',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <div className={'flex justify-end gap-2 pr-4'}>
          <ActionButton
            icon={PenIcon}
            variant={'grey'}
            onClick={(e) => {
              e.stopPropagation()
              toggleModal('update', row.original)
            }}
          />
          <ActionButton
            icon={DeleteIcon}
            variant={'danger'}
            onClick={(e) => {
              e.stopPropagation()
              toggleModal('delete', row.original)
            }}
          />
        </div>
      )
    }
  ]

  return (
    <InnerBox className={'p-4 rounded-2xl flex flex-col gap-3'}>
      <div className={'flex items-center gap-4'}>
        <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
          {"Lieux d'arrivée"}
        </Typography>

        <Button
          type={'button'}
          variant={'secondary'}
          label={"Ajouter un lieu d'arrivée"}
          icon={PlusIcon}
          onClick={() => toggleModal('create')}
        />
      </div>

      <DataTable<OtoIndividualPlace>
        columns={columns}
        data={otoIndividualPlaces ?? []}
        rowCount={otoIndividualPlaces?.length ?? 0}
        paginated={false}
        manualSorting={false}
        disableHover
      />

      <Modal
        isOpen={modalState.create}
        title={"Ajouter un lieu d'arrivée"}
        icon={LocationTwoIcon}
        hideActions
        size={'medium'}
        onCancel={() => closeModel('create')}
      >
        <OtoIndividualPlaceForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onConfirm: async (values) => {
              await createOtoIndividualPlaceMutation({ ...values, journeyPlaceId })
              closeModel('create')
            },
            onCancel: () => closeModel('create')
          })}
        />
      </Modal>

      <Modal
        isOpen={modalState.update}
        title={"Modifier le lieu d'arrivée"}
        icon={LocationTwoIcon}
        hideActions
        size={'medium'}
        onCancel={() => closeModel('update')}
      >
        {selectedData && (
          <OtoIndividualPlaceForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={{ title: selectedData.title, code: selectedData.code }}
            buttonsConfig={modalButtonConfig({
              onConfirm: async (values) => {
                await updateOtoIndividualPlaceMutation({
                  ...values,
                  id: selectedData.id,
                  journeyPlaceId
                })
                closeModel('update')
              },
              onCancel: () => closeModel('update')
            })}
          />
        )}
      </Modal>

      <Modal
        isOpen={modalState.delete}
        title={"Voulez-vous vraiment supprimer ce lieu d'arrivée ?"}
        icon={DeleteIcon}
        isLoading={isDeleting}
        size={'medium'}
        variant={'destructive'}
        onCancel={() => closeModel('delete')}
        actionButtonsOptions={{
          confirmText: 'Supprimer',
          cancelText: 'Fermer',
          onConfirm: () => {
            if (!selectedData) return
            deleteOtoIndividualPlaceMutation({
              journeyPlaceId,
              otoIndividualPlaceId: selectedData.id
            })
          }
        }}
      />
    </InnerBox>
  )
}

export default JourneyPlaceOtoIndividualPlaces
